import React, { useCallback } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/SossietyDialog';
import { Button } from '@/components/ui/button';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { PartnerFeedbackModal } from './PartnerFeedbackModal';
import { Training, TrainingPartner } from '@/types/trainings';
import { format } from 'date-fns';
import { TrainingNotesForm } from './TrainingNotesForm';
import { TrainingPartnersSection } from './TrainingPartnersSection';
import { TrainingTechniquesSection } from './TrainingTechniquesSection';
import { useTrainingNotesModal } from '@/features/calendar/hooks/useTrainingNotesModal';
import { supabase } from '@/integrations/supabase/client';
import { getTrainingCategoryTranslationKey } from '@/constants/trainingCategories';

interface TrainingNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  training: Training | null;
  notesData?: any;
  onSave?: (trainingId: string, notes: string) => void; // Keep for backward compatibility
  onDataSaved?: () => void; // New callback for when data is saved
}

export const TrainingNotesModal = ({ 
  isOpen, 
  onClose, 
  training, 
  notesData: propNotesData,
  onSave,
  onDataSaved
}: TrainingNotesModalProps) => {
  const { t, currentLanguage } = useI18n();
  
  // Use the hook for all state and logic
  const {
    // Form state
    fullNotes,
    setFullNotes,
    shareWithCommunity,
    setShareWithCommunity,
    privacyLevel,
    setPrivacyLevel,
    overallRating,
    setOverallRating,
    selectedTechniques,
    setSelectedTechniques,
    showTechniques,
    setShowTechniques,
    openCategories,
    setOpenCategories,
    
    // Partners state
    localPartners,
    partnersLoading,
    partnersSaving,
    partnerSearchValue,
    setPartnerSearchValue,
    reloadingPartnerNotes,
    hasPartnerNotes,
    getPartnerNotes,
    savePartners,
    removePartner,
    reloadPartnerNotes,
    updatePartnerNotes,
    partnerNotes,
    
    // Roll categories state
    rollCategories,
    rollCategoriesOpenCategories,
    setRollCategoriesOpenCategoriesForPartner,
    handleRollCategoryChange,
    getRollCategories,
    showRollCategories,
    setShowRollCategoriesForPartner,
    getShowRollCategories,
    
    // Feedback modal state
    currentFeedbackPartner,
    setCurrentFeedbackPartner,
    existingFeedback,
    setExistingFeedback,
    showFeedbackModal,
    setShowFeedbackModal,
    handleFeedbackModalClose,
    
    // Other state
    saving,
    
    // Data
    data,
    techniqueCategories,
    techniquesLoading,
    
    // Actions
    handleSave,
    handlePartnerSelect,
  } = useTrainingNotesModal({
    isOpen,
    training,
    notesData: propNotesData,
    onDataSaved,
  });

  // Handler for viewing partner feedback
  const handleViewFeedback = useCallback(async (partner: TrainingPartner): Promise<void> => {
    // Ensure we have the latest notes before opening the modal
    let notes = getPartnerNotes(partner);
    
    try {
      if (!notes) {
        // If notes are not in the map, fetch them directly
        if (!training?.id || (!partner.partner_user_id && !partner.id)) {
          // Even if we can't fetch, still open the modal with null notes
          setCurrentFeedbackPartner(partner);
          setExistingFeedback(null);
          setShowFeedbackModal(true);
          return;
        }

        const baseUrl = import.meta.env.DEV
          ? 'http://localhost:54321'
          : import.meta.env.VITE_SUPABASE_URL;
          
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        const response = await fetch(`${baseUrl}/functions/v1/get_partner_notes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            training_id: training.id,
            partner_user_id: partner.partner_user_id,
            partner_id: partner.id,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          notes = result.data;
          // Update the map in the hook for future use
          const key = partner.partner_user_id || partner.id;
          if (key) {
            updatePartnerNotes(key, notes);
          }
        }
      }
    } catch (error) {
      console.error('❌ [TrainingNotesModal] Error fetching partner notes:', error);
      notes = null;
    }
    
    // CRITICAL: Always execute these setStates, even if fetchPartnerNotes failed
    // This ensures the modal opens regardless of fetch success/failure
    setCurrentFeedbackPartner(partner);
    setExistingFeedback(notes);
    setShowFeedbackModal(true);
  }, [training?.id, getPartnerNotes, updatePartnerNotes, setCurrentFeedbackPartner, setExistingFeedback, setShowFeedbackModal]);

  // Handler for adding partner feedback
  const handleAddFeedback = useCallback(async (partner: TrainingPartner): Promise<void> => {
    const hasValidId = partner.id && partner.id !== '';
    
    try {
      if (hasValidId) {
        // Partner is already saved, just open modal
        setCurrentFeedbackPartner(partner);
        setExistingFeedback(null);
        setShowFeedbackModal(true);
      } else {
        // Partner not saved yet, save it first
        try {
          const savedPartner = await savePartners([partner]);
          
          if (savedPartner && savedPartner.length > 0) {
            const updatedPartner = savedPartner[0];
            setCurrentFeedbackPartner(updatedPartner);
            setExistingFeedback(null);
            setShowFeedbackModal(true);
          } else {
            throw new Error('No partner returned from save operation');
          }
        } catch (error) {
          console.error('Error saving partner before opening notes:', error);
          // Even if save fails, ensure setStates execute
          setCurrentFeedbackPartner(partner);
          setExistingFeedback(null);
          setShowFeedbackModal(true);
        }
      }
    } catch (error) {
      console.error('❌ [TrainingNotesModal] Error in Add Feedback onClick:', error);
      // Still open modal even if there was an error
      setCurrentFeedbackPartner(partner);
      setExistingFeedback(null);
      setShowFeedbackModal(true);
    }
  }, [savePartners, setCurrentFeedbackPartner, setExistingFeedback, setShowFeedbackModal]);

  // Handler for removing partner - wraps hook's removePartner
  const handleRemovePartner = useCallback(async (partnerId: string): Promise<void> => {
    try {
      if (partnerId && partnerId !== '') {
        removePartner(partnerId);
      }
    } catch (error) {
      console.error('Error in handleRemovePartner:', error);
    }
  }, [removePartner]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSaveAndClose = useCallback(async () => {
    try {
      await handleSave();
      // handleSave already calls onDataSaved, but we need to close the modal
      onClose();
    } catch (error) {
      // Error is already handled in handleSave, don't close modal on error
    }
  }, [handleSave, onClose]);

  if (!training) return null;

  const isCompleted = training.status === 'Completed';
  const hasNotes = !!(
    data?.notes ||
    (data?.techniques && data.techniques.length > 0) ||
    (data?.partners && data.partners.length > 0)
  );

  // Build training info string without empty values
  const getLocalizedTrainingType = (type: string): string => {
    switch (type) {
      case 'Group Class':
        return t('calendar.group_class');
      case 'Private Class':
        return t('calendar.private_class');
      default:
        return type;
    }
  };

  // Format date according to language
  const formatLocalizedDate = (date: Date): string => {
    if (currentLanguage === 'pt') {
      const month = date.toLocaleDateString('pt-BR', { month: 'long' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${day} de ${month} de ${year}`;
    } else {
      return format(date, 'MMMM d, yyyy');
    }
  };

  // Format time according to language
  const formatLocalizedTime = (date: Date): string => {
    if (currentLanguage === 'pt') {
      return format(date, 'HH:mm');
    } else {
      return format(date, 'h:mm a');
    }
  };

  // Helper to translate modality
  const getLocalizedModality = (modality: string): string => {
    if (modality === 'Gi') {
      return t('calendar.modality_gi');
    } else if (modality === 'No-Gi') {
      return t('calendar.modality_no_gi');
    }
    return modality;
  };

  // Helper to translate category
  const getLocalizedCategory = (category: string | undefined): string => {
    if (!category) return '';
    const categoryKey = getTrainingCategoryTranslationKey(category);
    return t(`calendar.training_categories.${categoryKey}`) || category;
  };

  const trainingInfoParts = [
    training.type ? getLocalizedTrainingType(training.type) : training.title,
    training.category ? getLocalizedCategory(training.category) : null,
    training.modality ? getLocalizedModality(training.modality) : null
  ].filter(Boolean);

  const trainingInfoString = trainingInfoParts.join(' - ');

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-2xl bg-white rounded-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden" aria-describedby="training-notes-description">
          <DialogDescription id="training-notes-description" className="sr-only">
            {hasNotes ? t('calendar.edit_notes') : t('calendar.training_notes_description')}
          </DialogDescription>
          <SossietyDialogHeader 
            title={hasNotes ? t('calendar.edit_notes') : t('calendar.add_notes')}
            titleClassName="text-lg"
          >
            <SossietyDialogClose onClick={handleClose} data-testid="training-notes-modal-close-button" />
          </SossietyDialogHeader>
          
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
            <div className="space-y-4">
              {/* Training Info - Type, Category, Modality */}
              <div className="rounded-lg">
                <h3 className="text-black font-sans text-sm font-semibold mb-1">
                  {trainingInfoString}
                </h3>
              </div>

              {/* Date */}
              <div className="rounded-lg !mt-0">
                <p className="text-gray-500 font-sans text-xs">
                  {formatLocalizedDate(new Date(training.start_time))} - {formatLocalizedTime(new Date(training.start_time))} {currentLanguage === 'pt' ? 'às' : 'to'} {formatLocalizedTime(new Date(training.end_time))}
                </p>
              </div>

              {/* Training Techniques Section */}
              <TrainingTechniquesSection
                selectedTechniques={selectedTechniques}
                setSelectedTechniques={setSelectedTechniques}
                showTechniques={showTechniques}
                setShowTechniques={setShowTechniques}
                openCategories={openCategories}
                setOpenCategories={setOpenCategories}
                techniqueCategories={techniqueCategories}
                techniquesLoading={techniquesLoading}
              />

              {/* Training Notes Form */}
              <TrainingNotesForm
                fullNotes={fullNotes}
                setFullNotes={setFullNotes}
                shareWithCommunity={shareWithCommunity}
                setShareWithCommunity={setShareWithCommunity}
                privacyLevel={privacyLevel}
                setPrivacyLevel={setPrivacyLevel}
                overallRating={overallRating}
                setOverallRating={setOverallRating}
                saving={saving}
              />

              {/* Training Partners Section */}
              <TrainingPartnersSection
                localPartners={localPartners}
                partnersLoading={partnersLoading}
                partnersSaving={partnersSaving}
                partnerSearchValue={partnerSearchValue}
                setPartnerSearchValue={setPartnerSearchValue}
                reloadingPartnerNotes={reloadingPartnerNotes}
                hasPartnerNotes={hasPartnerNotes}
                getPartnerNotes={getPartnerNotes}
                handlePartnerSelect={handlePartnerSelect}
                handleRemovePartner={handleRemovePartner}
                onViewFeedback={handleViewFeedback}
                onAddFeedback={handleAddFeedback}
                saving={saving}
                getRollCategories={getRollCategories}
                handleRollCategoryChange={handleRollCategoryChange}
                rollCategoriesOpenCategories={rollCategoriesOpenCategories}
                setRollCategoriesOpenCategoriesForPartner={setRollCategoriesOpenCategoriesForPartner}
                getShowRollCategories={getShowRollCategories}
                setShowRollCategoriesForPartner={setShowRollCategoriesForPartner}
              />
            </div>
          </div>

          {/* Fixed Action Button at bottom */}
          <div className="flex-shrink-0 pt-4 pb-6 px-6 border-t border-gray-200 bg-white">
            <Button
              className="w-full bg-sossiety-red hover:bg-sossiety-red-hover text-white font-medium py-2.5 h-10 rounded-full transition-colors duration-200 disabled:opacity-50"
              onClick={handleSaveAndClose}
              disabled={saving || partnersSaving}
              data-testid="training-notes-modal-update-button"
            >
              {saving || partnersSaving ? t('calendar.saving') : (hasNotes ? t('calendar.update_notes') : t('calendar.save_notes'))}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partner Notes Modal - Outside Dialog */}
      {currentFeedbackPartner && (
        <PartnerFeedbackModal
          isOpen={showFeedbackModal}
          onClose={async (wasSaved = false) => {
            if (wasSaved && currentFeedbackPartner) {
              const partnerKey = currentFeedbackPartner.partner_user_id || currentFeedbackPartner.id;
              if (partnerKey) {
                await reloadPartnerNotes(partnerKey);
              }
            }
            handleFeedbackModalClose(wasSaved);
          }}
          partner={currentFeedbackPartner}
          trainingId={training?.id || null}
          trainingNotesId={data?.notes?.id || null}
          existingNotes={existingFeedback}
          mode={existingFeedback ? 'view' : 'create'}
          rollCategories={getRollCategories(currentFeedbackPartner)}
          onRollCategoriesChange={(categories) => {
            const partnerKey = currentFeedbackPartner.partner_user_id || currentFeedbackPartner.id;
            Object.keys(categories).forEach((category) => {
              handleRollCategoryChange(partnerKey, category, categories[category as keyof typeof categories] || '');
            });
          }}
          getRollCategories={() => getRollCategories(currentFeedbackPartner)}
        />
      )}
    </>
  );
};
